import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { testsService } from '@/services/tests.service';
import { examsService } from '@/services/exams.service';
import { coursesService } from '@/services/courses.service';
import { Plus, Search, Edit, Trash2, FileCheck, Clock } from 'lucide-react';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';
import type { Test } from '@/types';

export function TestsList() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [examFilter, setExamFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Fetch tests
  const { data: testsData, isLoading } = useQuery({
    queryKey: ['tests', { search, course: courseFilter, exam: examFilter }],
    queryFn: async () => {
      try {
        const filters: any = { search };
        if (courseFilter !== 'all') filters.course_id = courseFilter;
        if (examFilter !== 'all') filters.exam_id = examFilter;
        return await testsService.getTests(filters);
      } catch (error) {
        console.error('Failed to fetch tests:', error);
        return [];
      }
    },
  });

  // Fetch exams for filter
  const { data: exams = [] } = useQuery({
    queryKey: ['exams'],
    queryFn: async () => {
      try {
        return await examsService.getExams({ status: 'active' });
      } catch (error) {
        return [];
      }
    },
  });

  // Fetch courses for filter
  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      try {
        return await coursesService.getCourses();
      } catch (error) {
        return [];
      }
    },
  });

  const allTests = testsData || [];
  const totalItems = allTests.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const tests = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return allTests.slice(startIndex, endIndex);
  }, [allTests, currentPage, itemsPerPage]);

  const deleteMutation = useMutation({
    mutationFn: async (testId: string) => {
      return await testsService.deleteTest(testId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tests'] });
      showDeleteSuccess('Test has been deleted successfully.');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to delete test';
      showDeleteError(message);
    },
  });

  const handleDeleteClick = async (testId: string, testName: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Test?',
      itemName: testName,
    });

    if (confirmed) {
      deleteMutation.mutate(testId);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tests</h1>
          <p className="text-gray-600 mt-1">Manage course tests and question assignments</p>
        </div>
        <Link to="/tests/create" className="btn-primary flex items-center">
          <Plus className="w-4 h-4 mr-2" />
          Create Test
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Tests</p>
              <p className="text-3xl font-bold text-gray-900">{allTests.length}</p>
            </div>
            <FileCheck className="w-10 h-10 text-blue-500" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-3xl font-bold text-gray-900">
                {allTests.filter((t: Test) => t.is_active).length}
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-full">
              <FileCheck className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Mock Exams</p>
              <p className="text-3xl font-bold text-gray-900">
                {allTests.filter((t: Test) => t.test_type === 'mock_exam').length}
              </p>
            </div>
            <div className="p-2 bg-fk-orange-vibrant100 rounded-full">
              <FileCheck className="w-6 h-6 text-#ea7500" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Practice Tests</p>
              <p className="text-3xl font-bold text-gray-900">
                {allTests.filter((t: Test) => t.test_type === 'practice_test').length}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-full">
              <FileCheck className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute w-5 h-5 text-gray-400 left-3 top-2.5" />
              <input
                type="text"
                placeholder="Search tests..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 input-field"
              />
            </div>
          </div>
          <select
            value={examFilter}
            onChange={(e) => setExamFilter(e.target.value)}
            className="input-field w-48"
          >
            <option value="all">All Exams</option>
            {exams.map(exam => (
              <option key={exam.exam_id} value={exam.exam_id}>{exam.exam_id}</option>
            ))}
          </select>
          <select
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
            className="input-field w-48"
          >
            <option value="all">All Courses</option>
            {courses.map(course => (
              <option key={course.course_id} value={course.course_id}>{course.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tests Table */}
      <div className="card">
        {isLoading ? (
          <div className="py-12 text-center text-gray-500">Loading...</div>
        ) : tests.length === 0 ? (
          <div className="py-12 text-center">
            <FileCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No tests yet</h3>
            <p className="text-gray-600 mb-4">Create your first test to get started</p>
            <Link to="/tests/create" className="btn-primary inline-flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              Create Test
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Test ID</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Exam</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Questions</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tests.map((test: Test) => (
                  <tr key={test.test_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">{test.test_id}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{test.test_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{test.course_id}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{test.exam_id}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                        {test.test_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{test.total_questions}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {test.duration_minutes} min
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {test.is_active ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Inactive</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/tests/${test.test_id}/edit`}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteClick(test.test_id, test.test_name)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} tests
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
